<template>
  <vx-card class="crew-assignment-day-off-purchase-carryover">
    <div class="header">
      <div class="left-content">
        <div class="title">休暇買上げ・繰越し</div>
        <v-select
          class="select-year"
          v-model="year"
          label="label"
          :options="yearOptions"
          :reduce="option => option.id"
          :disabled="isEdit"
          :clearable="false"
          @input="onYearChange"
        >
          <template v-slot:selected-option="slotProps">
            <span v-if="slotProps && slotProps.label">
              年度切替（{{ slotProps.label }}年度）
            </span>
            <span v-else>年度切替</span>
          </template>

          <template v-slot:no-options>-</template>
        </v-select>
      </div>
      <div class="right-content">
        <Button
          type="border"
          icon="edit"
          colorType="normal"
          label="編集"
          :disabled="isEdit || filteredRows.length === 0"
          @click="onEditClick"
        />
      </div>
    </div>
    <div class="select-option-container">
      <!-- 船舶会社 -->
      <SelectInput
        class="w-48"
        v-model="selectedFilterVesselCompanyIds"
        :options="vesselCompanyOptions"
        :multiple="true"
        :clearable="true"
        placeholder="船舶会社"
        :limit="1"
        :limitText="count => `+${count}`"
        :disabled="isEdit"
      ></SelectInput>
      <!-- 社員区分 -->
      <SelectInput
        class="w-48"
        v-model="selectedFilterEmploymentStatus"
        :options="employmentStatusFilterOptions"
        :multiple="true"
        :clearable="true"
        placeholder="社員区分"
        :limit="1"
        :limitText="count => `+${count}`"
        :disabled="isEdit"
      ></SelectInput>
      <!-- 辞令職 -->
      <SelectInput
        class="w-48"
        v-model="selectedFilterAppointmentRoleIds"
        :options="appointmentRoleOptions"
        :multiple="true"
        :clearable="true"
        placeholder="辞令職"
        :limit="1"
        :limitText="count => `+${count}`"
        :disabled="isEdit"
      ></SelectInput>
      <!-- 船員名 -->
      <SelectInput
        class="w-48"
        v-model="selectedFilterCrewIds"
        :options="crewOptions"
        :multiple="true"
        :clearable="true"
        placeholder="船員名"
        :limit="1"
        :limitText="count => `+${count}`"
        :disabled="isEdit"
      ></SelectInput>
      <!-- 在籍状況 -->
      <SelectInput
        class="w-48"
        v-model="selectedFilterOnboardingStatus"
        :options="onboardingStatusOptions"
        :multiple="true"
        :clearable="true"
        placeholder="在籍状況"
        :limit="1"
        :limitText="count => `+${count}`"
        :disabled="isEdit"
      ></SelectInput>
    </div>

    <div class="grid-wrapper">
      <ag-grid-vue
        ref="agGridTableDayOffPurchaseCarryover"
        :gridOptions="gridOptions"
        class="ag-theme-material w-full ag-grid-table"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef"
        :rowData="filteredRows"
        rowSelection="multiple"
        colResizeDefault="shift"
        :animateRows="true"
        :floatingFilter="false"
        :suppressPaginationPanel="true"
        :suppressDragLeaveHidesColumns="true"
        :suppressRowClickSelection="true"
        :suppressCellFocus="true"
        :suppressNoRowsOverlay="false"
        @grid-ready="onGridReady"
        domLayout="autoHeight"
      />
    </div>

    <div class="bottom-space">
      <template v-if="isEdit">
        <Button
          type="border"
          colorType="cancel"
          label="編集取消"
          @click="cancelEdit"
        />
        <Button
          type="filled"
          colorType="normal"
          label="保存"
          :disabled="hasValidationErrors"
          @click="saveEdits"
        />
      </template>
      <template v-else>
        <Button
          type="filled"
          colorType="normal"
          label="一括締め"
          :disabled="filteredRows.length === 0"
          @click="onBulkClose"
        />
      </template>
    </div>
  </vx-card>
</template>

<script>
import moment from "moment-timezone";
import { mapState } from "vuex";
import { AgGridVue } from "ag-grid-vue";
import vSelect from "vue-select";
import { DeviceInfoMixin, ColorMixin } from "@/mixins";
import "@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss";
import SelectInput from "../components/SelectInput.vue";
import Button from "../components/Button.vue";
import CellRendererCrewInfo from "./cell-renderer/CellRendererCrewInfo.vue";
import CellRendererActions from "./cell-renderer/CellRendererActions.vue";
import CrewAssignmentManagementService from "../servise/Service";
import CellRendererPurchasedHolidayDaysInput from "./cell-renderer/CellRendererPurchasedHolidayDaysInput.vue";
import CellRendererRolloverHolidayDaysInput from "./cell-renderer/CellRendererRolloverHolidayDaysInput.vue";
import CellRendererPurchasedPayedHolidayDaysInput from "./cell-renderer/CellRendererPurchasedPayedHolidayDaysInput.vue";
import CellRendererRolloverPayedHolidayDaysInput from "./cell-renderer/CellRendererRolloverPayedHolidayDaysInput.vue";
import CellRendererRemarksInput from "./cell-renderer/CellRendererRemarksInput.vue";
import CellRendererCrewStatus from "./cell-renderer/CellRendererCrewStatus.vue";
import {
  getEmploymentStatusLabel,
  EmploymentStatusOptionsForTree,
} from "@/views/enterprise/management/crews/crew-settings/constants/EmploymentStatus.js";
import { getCurrentFiscalYear } from "@/utils/DateUtil";

export default {
  name: "CrewAssignmentDayOffPurchaseCarryover",
  mixins: [DeviceInfoMixin, ColorMixin],
  components: {
    vSelect,
    SelectInput,
    AgGridVue,
    Button,
    CellRendererCrewInfo,
    CellRendererActions,
    CellRendererPurchasedHolidayDaysInput,
    CellRendererRolloverHolidayDaysInput,
    CellRendererPurchasedPayedHolidayDaysInput,
    CellRendererRolloverPayedHolidayDaysInput,
    CellRendererRemarksInput,
    CellRendererCrewStatus,
  },
  data() {
    return {
      isEdit: false,

      crewFiscalYearSummariesData: [],

      year: null,
      yearOptions: [],
      validationErrors: {},

      vesselCompanyOptions: [],
      employmentStatusFilterOptions: EmploymentStatusOptionsForTree.map(
        option => ({
          ...option,
        })
      ),
      appointmentRoleOptions: [],
      crewOptions: [],
      onboardingStatusOptions: [
        {
          id: "active",
          label: "在籍中",
        },
        {
          id: "inactive",
          label: "退職済",
        },
      ],

      selectedFilterVesselCompanyIds: [],
      selectedFilterEmploymentStatus: [],
      selectedFilterAppointmentRoleIds: [],
      selectedFilterCrewIds: [],
      selectedFilterOnboardingStatus: [],

      selectedCrewId: null,

      tableRows: [],
      beforeEditSnapshot: [],
      originalTableRows: [],
      cellValidationState: {},

      gridApi: null,
      gridColumnApi: null,
      gridOptions: {
        localeText: {
          noRowsToShow:
            '<div id="no-rows-crew-list" class="aisea-text-16-400-black">表示する休暇買上げ・繰越し情報がありません</div>',
        },
        context: {
          componentParent: this,
        },
        suppressKeyboardEvent: params => {
          const event = params.event;
          const key = event.key;

          // 押されたのが矢印キーかどうかを判定
          const isArrowKey = [
            "ArrowUp",
            "ArrowDown",
            "ArrowLeft",
            "ArrowRight",
          ].includes(key);

          if (!isArrowKey) {
            return false; // 矢印キー以外（文字入力など）は AG Grid に任せる
          }

          // 現在フォーカスがある要素（target）を取得
          const target = event.target;

          // フォーカスが INPUT または TEXTAREA にある場合
          if (
            target &&
            (target.tagName === "INPUT" || target.tagName === "TEXTAREA")
          ) {
            return true; // AG Grid のセル移動機能を無効化
          }
          return false;
        },
      },
      defaultColDef: {
        resizable: true,
        sortable: false,
        autoHeight: true,
        wrapText: true,
        icons: {
          sortAscending:
            '<span class="material-icons">keyboard_arrow_up</span>',
          sortDescending:
            '<span class="material-icons">keyboard_arrow_down</span>',
          sortUnSort: '<span class="material-icons">unfold_more</span>',
        },
        unSortIcon: true,
        comparator: () => {
          return;
        },
      },
    };
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
    }),
    columnDefs() {
      const columns = [
        {
          headerName: "船員名\n所属会社",
          field: "crewInfo",
          width: 240,
          pinned: "left",
          autoHeight: true,
          cellClass: "cell-view-only-left",
          cellRenderer: "CellRendererCrewInfo",
          cellRendererParams: {
            onClickNameLink: data => {
              const routeData = this.$router.resolve({
                name: "enterprise-management-crew-settings-assignment",
                params: { id: data },
              });
              window.open(routeData.href, "_blank");
            },
          },
        },
        {
          headerName: "役職名",
          field: "roleName",
          minWidth: 120,
          flex: 1,
          cellClass: "cell-view-only-left",
        },
        {
          headerName: "社員区分",
          field: "employmentStatus",
          minWidth: 80,
          autoHeight: true,
          flex: 1,
          cellClass: "cell-view-only-left",
        },
        {
          headerName: "在籍状態",
          field: "onboardingStatusLabel",
          minWidth: 80,
          autoHeight: true,
          flex: 1,
          cellClass: "cell-view-only-left",
          cellRenderer: "CellRendererCrewStatus",
        },
        {
          headerName: "必要\n休日数",
          field: "basicHolidayDays",
          minWidth: 72,
          autoHeight: true,
          flex: 1,
          cellClass: "cell-view-only-left",
        },
        {
          headerName: "消化\n休日数",
          field: "takenHolidayDays",
          minWidth: 72,
          autoHeight: true,
          flex: 1,
          cellClass: "cell-view-only-left",
        },
        {
          headerName: "残\n休日数",
          field: "remainingDays",
          minWidth: 72,
          autoHeight: true,
          flex: 1,
          cellClass: "cell-view-only-left",
        },
        {
          headerName: "有給\n日数",
          field: "basicPayedHolidayDays",
          minWidth: 72,
          autoHeight: true,
          flex: 1,
          cellClass: "cell-view-only-left",
        },
        {
          headerName: "消化\n有給数",
          field: "takenPayedHolidayDays",
          minWidth: 72,
          autoHeight: true,
          flex: 1,
          cellClass: "cell-view-only-left",
        },
        {
          headerName: "残\n有給数",
          field: "remainingPaidLeaveDays",
          minWidth: 72,
          autoHeight: true,
          flex: 1,
          cellClass: "cell-view-only-left",
        },
        {
          headerName: "休暇\n買上げ",
          field: "purchasedHolidayDays",
          width: 72,
          pinned: "right",
          autoHeight: true,
          cellClass: this.isEdit ? "cell-edit-right" : "cell-view-only-left",
          cellRenderer: "CellRendererPurchasedHolidayDaysInput",
        },
        {
          headerName: "休暇\n繰越し",
          field: "rolloverHolidayDays",
          width: 72,
          pinned: "right",
          autoHeight: true,
          cellClass: this.isEdit ? "cell-edit-right" : "cell-view-only-left",
          cellRenderer: "CellRendererRolloverHolidayDaysInput",
        },
        {
          headerName: "有給\n買上げ",
          field: "purchasedPayedHolidayDays",
          width: 72,
          pinned: "right",
          autoHeight: true,
          cellClass: this.isEdit ? "cell-edit-right" : "cell-view-only-left",
          cellRenderer: "CellRendererPurchasedPayedHolidayDaysInput",
        },
        {
          headerName: "有給\n繰越し",
          field: "rolloverPayedHolidayDays",
          width: 72,
          pinned: "right",
          autoHeight: true,
          cellClass: this.isEdit ? "cell-edit-right" : "cell-view-only-left",
          cellRenderer: "CellRendererRolloverPayedHolidayDaysInput",
        },
        {
          headerName: "備考",
          field: "remarks",
          width: 150,
          pinned: "right",
          autoHeight: true,
          wrapText: true,
          cellClass: this.isEdit ? "cell-edit-left" : "cell-view-only-left",
          cellRenderer: "CellRendererRemarksInput",
        },
      ];

      if (this.isEdit) return columns;

      return [
        ...columns,
        {
          headerName: "最終更新者",
          field: "lastUpdatedBy",
          width: 100,
          pinned: "right",
          autoHeight: true,
          cellClass: "cell-view-only-left",
        },
        {
          headerName: "最終更新日時",
          field: "lastUpdatedAt",
          width: 120,
          pinned: "right",
          autoHeight: true,
          cellClass: "cell-view-only-left",
          valueFormatter: params => {
            const v = params.value;
            if (!v || v === "-") return "-";
            // 日付と時間をスペースで分割して改行
            return String(v).replace(" ", "\n");
          },
          cellStyle: { whiteSpace: "pre-line" },
        },
        {
          headerName: "操作",
          field: "actions",
          width: 100,
          pinned: "right",
          autoHeight: true,
          cellClass: "cell-view-only-center",
          cellRenderer: "CellRendererActions",
          cellRendererParams: {
            onClickEdit: rowData => {
              if (!rowData || !rowData.crewInfo) return;
              const crewName = rowData.crewInfo.name || "船員";
              const isClosed = rowData.isClosed;

              if (isClosed) {
                // 引戻し処理
                this.$vs.dialog({
                  type: "confirm",
                  color: "dark",
                  title: "引戻し",
                  text: `${crewName}の買上げ・繰越を引戻しますか？`,
                  acceptText: "実行",
                  cancelText: "キャンセル",
                  accept: async () => {
                    // 引戻し処理実行
                    try {
                      this.$vs.loading({ color: this.AISEA_COLOR.BLUE });
                      await this.updateCrewFiscalYear(rowData, "PULL_BACK");
                      await this.fetchCrewFiscalYearSummaries(this.year); // 再取得
                      this.$vs.notify({
                        title: "引戻し",
                        text: "行の引戻しが成功しました。",
                        color: "success",
                        iconPack: "feather",
                        icon: "icon-check",
                      });
                    } catch (e) {
                      this.$vs.notify({
                        title: "引戻しの失敗",
                        text:
                          "引戻しに失敗しました。時間を空けて再度試みてください。",
                        color: "danger",
                        iconPack: "feather",
                        icon: "icon-alert-circle",
                      });
                    } finally {
                      this.$vs.loading.close();
                    }
                  },
                });
              } else {
                // 締め処理
                const warningMessages = this.collectCarryoverWarningMessages([
                  rowData,
                ]);
                const baseText = `${crewName}の買上げ・繰越を締めしますか？`;
                const dialogText = this.buildDialogText(
                  warningMessages,
                  baseText
                );
                this.$vs.dialog({
                  type: "confirm",
                  color: "dark",
                  title: "締め",
                  text: dialogText,
                  acceptText: "実行",
                  cancelText: "キャンセル",
                  accept: async () => {
                    try {
                      this.$vs.loading({ color: this.AISEA_COLOR.BLUE });
                      await this.updateCrewFiscalYear(rowData, "CLOSE");
                      await this.fetchCrewFiscalYearSummaries(this.year); // 再取得
                      this.$vs.notify({
                        title: "締め",
                        text: "行の締めが成功しました。",
                        color: "success",
                        iconPack: "feather",
                        icon: "icon-check",
                      });
                    } catch (e) {
                      this.$vs.notify({
                        title: "締めの失敗",
                        text:
                          "締めに失敗しました。時間を空けて再度試みてください。",
                        color: "danger",
                        iconPack: "feather",
                        icon: "icon-alert-circle",
                      });
                    } finally {
                      this.$vs.loading.close();
                    }
                  },
                });
              }
            },
          },
        },
      ];
    },
    filteredRows() {
      return this.tableRows.filter(row => {
        const matchesVessel =
          this.selectedFilterVesselCompanyIds.length > 0
            ? this.selectedFilterVesselCompanyIds.includes(row.crewInfo.company)
            : true;
        const matchesEmployment =
          this.selectedFilterEmploymentStatus.length > 0
            ? this.selectedFilterEmploymentStatus.includes(
                row.employmentStatusValue
              )
            : true;
        const matchesRole =
          this.selectedFilterAppointmentRoleIds.length > 0
            ? this.selectedFilterAppointmentRoleIds.includes(
                row.crewInfo.roleId
              )
            : true;
        const matchesCrew =
          this.selectedFilterCrewIds.length > 0
            ? this.selectedFilterCrewIds.includes(row.crewInfo.id)
            : true;
        const matchesOnboarding =
          this.selectedFilterOnboardingStatus.length > 0
            ? this.selectedFilterOnboardingStatus.includes(row.onboardingStatus)
            : true;

        return (
          matchesVessel &&
          matchesEmployment &&
          matchesRole &&
          matchesCrew &&
          matchesOnboarding
        );
      });
    },
    hasValidationErrors() {
      return Object.values(this.cellValidationState).some(rowState => {
        if (!rowState) {
          return false;
        }
        return Object.values(rowState).some(
          state => state && state.isValid === false
        );
      });
    },
  },
  methods: {
    async fetchCrewRoles() {
      try {
        const response = await CrewAssignmentManagementService.fetchCrewRoles();
        //辞令職選択肢の初期化
        this.appointmentRoleOptions = response.data.roles.map(role => {
          return { id: role.id, label: role.name };
        });
      } catch (error) {
        console.error(error);
        this.$vs.notify({
          title: "辞令職情報取得エラー",
          text: "辞令職情報の取得に失敗しました",
          color: "danger",
          iconPack: "feather",
          icon: "icon-alert-circle",
        });
      }
    },

    async fetchFiscalYearDefaults() {
      try {
        const response = await CrewAssignmentManagementService.fetchFiscalYearDefaults();
        // 年度選択肢の初期化（降順：新しい年度が上）
        this.yearOptions = response.data.fiscal_year_defaults
          .map(item => ({
            id: String(item.fiscal_year),
            label: String(item.fiscal_year),
          }))
          .sort((a, b) => Number(b.id) - Number(a.id));
        // 年度選択状況の初期化（当年度を選択、当年度がなければ最新年度）
        if (!this.yearOptions || this.yearOptions.length === 0) return null;
        const currentFiscal = String(getCurrentFiscalYear());
        const found = this.yearOptions.find(o => o.id === currentFiscal);
        this.year = found ? found.id : this.yearOptions[0].id;
      } catch (error) {
        console.error(error);
        this.$vs.notify({
          title: "年度情報取得エラー",
          text: "年度情報の取得に失敗しました",
          color: "danger",
          iconPack: "feather",
          icon: "icon-alert-circle",
        });
      }
    },

    async fetchCrewFiscalYearSummaries(year) {
      try {
        const response = await CrewAssignmentManagementService.fetchCrewFiscalYearSummaries(
          year
        );
        this.crewFiscalYearSummariesData =
          response.data.crew_fiscal_year_summaries || [];

        // 所属会社選択肢の初期化
        const uniqueCompanyNames = [
          ...new Set(
            this.crewFiscalYearSummariesData
              .map(item => item.affiliated_company_name)
              .filter(Boolean)
          ),
        ];
        this.vesselCompanyOptions = uniqueCompanyNames.map(name => ({
          id: name,
          label: name,
        }));

        // 船員選択肢の初期化
        this.crewOptions = this.crewFiscalYearSummariesData.map(item => ({
          id: item.enterprise_crew_id,
          label: item.name,
        }));

        // tableRowsの初期化
        this.tableRows = this.crewFiscalYearSummariesData.map(item => {
          const role = this.appointmentRoleOptions.find(role => {
            return role.id === item.enterprise_crew_role_id;
          });

          // 残休日数（0未満にならないように）
          const remainingDays = Math.max(
            (item.basic_holiday_days || 0) - (item.taken_holiday_days || 0),
            0
          );

          // 残有給数（0未満にならないように）
          const remainingPaidLeaveDays = Math.max(
            (item.basic_payed_holiday_days || 0) -
              (item.taken_payed_holiday_days || 0),
            0
          );

          return {
            id: item.id,

            // 船員名 / 役職名 / 所属会社
            crewInfo: {
              id: item.enterprise_crew_id,
              name: item.name,
              company: item.affiliated_company_name || (this.user && this.user.name ? this.user.name : ""),
              roleId: role ? role.id : "",
            },

            roleName: role ? role.label : "-",

            // 基本情報
            employmentStatus: getEmploymentStatusLabel(item.status),
            employmentStatusValue: item.status,
            onboardingStatus: item.active ? "active" : "inactive",
            onboardingStatusLabel: item.active ? "在籍" : "退職済み",

            // 休日関連
            basicHolidayDays: item.basic_holiday_days,
            takenHolidayDays: item.taken_holiday_days,
            remainingDays: remainingDays,

            // 有給関連
            basicPayedHolidayDays: item.basic_payed_holiday_days,
            takenPayedHolidayDays: item.taken_payed_holiday_days,
            remainingPaidLeaveDays: remainingPaidLeaveDays,

            // 入力・操作項目
            purchasedHolidayDays: item.purchased_holiday_days,
            rolloverHolidayDays: item.rollover_holiday_days,
            purchasedPayedHolidayDays: item.purchased_payed_holiday_days,
            rolloverPayedHolidayDays: item.rollover_payed_holiday_days,

            remarks: item.remarks,

            // 操作列（締め・引戻し）の判定に使用
            isClosed: item.finalized,

            lastUpdatedBy: item.updater || "-",
            lastUpdatedAt: item.updated_at
              ? moment(item.updated_at)
                  .local()
                  .format("YYYY/MM/DD HH:mm")
              : "-",
          };
        });

        // 編集判定用のオリジナルデータを保持
        this.originalTableRows = this.cloneRows(this.tableRows);
        this.beforeEditSnapshot = [];
      } catch (error) {
        console.error(error);
        // 404エラー（データが見つからない）の場合はエラー通知を表示しない
        if (error && error.code === 404) {
          // データが存在しない場合は空配列を設定
          this.crewFiscalYearSummariesData = [];
          this.vesselCompanyOptions = [];
          this.crewOptions = [];
          this.tableRows = [];
          this.originalTableRows = [];
          this.beforeEditSnapshot = [];
          return;
        }
        // 404以外のエラーの場合はエラー通知を表示
        this.$vs.notify({
          title: "休暇買上げ・繰越し情報取得エラー",
          text: "休暇買上げ・繰越しの取得に失敗しました",
          color: "danger",
          iconPack: "feather",
          icon: "icon-alert-circle",
        });
      }
    },

    async updateCrewFiscalYear(rowData, type) {
      const summary = this.crewFiscalYearSummariesData.find(
        item => item.id === rowData.id
      );
      if (!summary) {
        throw new Error("対象の船員年度情報が見つかりません");
      }

      // type に基づいて finalized の状態を決定
      let finalizedStatus = summary.finalized;
      if (type === "CLOSE") finalizedStatus = true;
      if (type === "PULL_BACK") finalizedStatus = false;

      const payload = {
        basic_ashore_off_days: summary.basic_ashore_off_days,
        basic_boarding_days: summary.basic_boarding_days,
        basic_holiday_days: summary.basic_holiday_days,
        basic_payed_holiday_days: summary.basic_payed_holiday_days,

        // 更新項目
        purchased_holiday_days: rowData.purchasedHolidayDays, // 休暇買上げ
        rollover_holiday_days: rowData.rolloverHolidayDays, // 休暇繰越し
        purchased_payed_holiday_days: rowData.purchasedPayedHolidayDays, // 有給買上げ
        rollover_payed_holiday_days: rowData.rolloverPayedHolidayDays, // 有給繰越し
        remarks: rowData.remarks,
        finalized: finalizedStatus,
      };
      return await CrewAssignmentManagementService.updateCrewFiscalYear(
        summary.enterprise_crew_id,
        summary.id,
        payload
      );
    },

    async onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.$vs.loading({ color: this.AISEA_COLOR.BLUE });
      await this.fetchCrewRoles();
      await this.fetchFiscalYearDefaults();
      await this.fetchCrewFiscalYearSummaries(this.year);
      this.$vs.loading.close();
    },

    async onYearChange(selectedYear) {
      this.clearErrors();
      this.year = selectedYear;
      this.$vs.loading({ color: this.AISEA_COLOR.BLUE });
      await this.fetchCrewFiscalYearSummaries(this.year);
      this.$vs.loading.close();
    },

    onEditClick() {
      if (this.isEdit) {
        return;
      }
      this.beforeEditSnapshot = this.cloneRows(this.tableRows);
      this.isEdit = true;
      this.resetCellValidationState();
      this.refreshEditableCells();
    },

    cancelEdit() {
      if (!this.isEdit) {
        return;
      }
      const sourceRows =
        (this.beforeEditSnapshot && this.beforeEditSnapshot.length > 0
          ? this.beforeEditSnapshot
          : this.originalTableRows) || [];
      this.tableRows = this.cloneRows(sourceRows);
      this.isEdit = false;
      this.beforeEditSnapshot = [];
      this.resetCellValidationState();
      this.$nextTick(() => {
        this.refreshEditableCells();
      });
    },

    async saveEdits() {
      if (this.hasValidationErrors) {
        this.$vs.notify({
          title: "入力内容を確認してください",
          text: "エラーが解消されるまで保存できません",
          color: "warning",
          iconPack: "feather",
          icon: "icon-alert-triangle",
        });
        return;
      }

      const warningMessages = this.collectCarryoverWarningMessages(
        this.filteredRows
      );
      const baseText = "編集内容を保存しますか？";
      const dialogText = this.buildDialogText(warningMessages, baseText);

      this.$vs.dialog({
        type: "confirm",
        color: "dark",
        title: "編集",
        text: dialogText,
        acceptText: "保存",
        cancelText: "キャンセル",
        accept: async () => {
          await this.performSaveEdits();
        },
      });
    },

    async performSaveEdits() {
      try {
        this.$vs.loading({ color: this.AISEA_COLOR.BLUE });
        // 編集中のフィルタリングされたデータのみを更新
        const promises = this.filteredRows.map(row => {
          if (!row.isClosed) {
            // 締め済みでない場合のみを更新対象とする
            return this.updateCrewFiscalYear(row, "EDIT");
          }
        });
        await Promise.all(promises);

        // 成功後の処理
        this.originalTableRows = this.cloneRows(this.tableRows);
        this.beforeEditSnapshot = [];
        this.isEdit = false;
        this.resetCellValidationState();
        this.refreshEditableCells();

        // データ再取得（最新の更新日時などを反映させるため推奨）
        await this.fetchCrewFiscalYearSummaries(this.year);

        this.$vs.notify({
          title: "買上・繰越しの編集",
          text: "買上・繰越しの編集に成功しました。",
          color: "success",
          iconPack: "feather",
          icon: "icon-check",
        });

        this.beforeEditSnapshot = [];
        this.isEdit = false;
      } catch (error) {
        this.$vs.notify({
          title: "買上・繰越しの編集",
          text:
            "買上・繰越しの編集に失敗しました。時間を空けて再度試みてください。",
          color: "danger",
          iconPack: "feather",
          icon: "icon-alert-circle",
        });
      } finally {
        this.$vs.loading.close();
      }
    },

    async onBulkClose() {
      const warningMessages = this.collectCarryoverWarningMessages(
        this.filteredRows
      );
      const baseText = "買上げ・繰越しを一括締めしますか？";
      const dialogText = this.buildDialogText(warningMessages, baseText);
      this.$vs.dialog({
        type: "confirm",
        color: "dark",
        title: "一括締め",
        text: dialogText,
        acceptText: "実行",
        cancelText: "キャンセル",
        accept: async () => {
          try {
            this.$vs.loading({ color: this.AISEA_COLOR.BLUE });

            // filteredRows（フィルタ後のデータ）を対象にする
            const promises = this.filteredRows.map(row =>
              this.updateCrewFiscalYear(row, "CLOSE")
            );
            await Promise.all(promises);

            await this.fetchCrewFiscalYearSummaries(this.year);
            this.$vs.notify({
              title: "一括締め",
              text: "一括締めが成功しました。",
              color: "success",
              iconPack: "feather",
              icon: "icon-check",
            });
          } catch (error) {
            this.$vs.notify({
              title: "一括締めの失敗",
              text: "一括締めに失敗しました。時間を空けて再度試みてください。",
              color: "danger",
              iconPack: "feather",
              icon: "icon-alert-circle",
            });
          } finally {
            this.$vs.loading.close();
          }
        },
      });
    },

    handleCellValidation(rowId, field, result) {
      if (!rowId || !field) {
        return;
      }
      if (!this.cellValidationState[rowId]) {
        this.$set(this.cellValidationState, rowId, {});
      }
      this.$set(this.cellValidationState[rowId], field, result);
    },

    resetCellValidationState() {
      this.cellValidationState = {};
    },

    refreshEditableCells() {
      if (!this.gridApi) {
        return;
      }
      this.gridApi.refreshCells({ force: true });
      this.gridApi.resetRowHeights();
    },

    collectCarryoverWarningMessages(rows = []) {
      return rows.reduce((messages, row) => {
        if (!row || row.isClosed) {
          return messages;
        }

        const remainingHoliday = Number(row.remainingDays) || 0;
        const purchasedHoliday = Number(row.purchasedHolidayDays) || 0;
        const holidayLimit = Math.max(remainingHoliday - purchasedHoliday, 0);
        const rolloverHoliday = Number(row.rolloverHolidayDays) || 0;

        const remainingPaid = Number(row.remainingPaidLeaveDays) || 0;
        const purchasedPaid = Number(row.purchasedPayedHolidayDays) || 0;
        const paidLimit = Math.max(remainingPaid - purchasedPaid, 0);
        const rolloverPaid = Number(row.rolloverPayedHolidayDays) || 0;

        const holidayMismatch = rolloverHoliday !== holidayLimit;
        const paidMismatch = rolloverPaid !== paidLimit;

        if (!holidayMismatch && !paidMismatch) {
          return messages;
        }

        const targets = [];
        if (holidayMismatch) targets.push("休暇繰越し");
        if (paidMismatch) targets.push("有給繰越し");

        const crewName =
          (row.crewInfo && row.crewInfo.name) ||
          (row.crewInfo && row.crewInfo.id
            ? `ID:${row.crewInfo.id}`
            : `ID:${row.id}`);

        messages.push(`${crewName}：${targets.join("・")}の値が不一致です。`);
        return messages;
      }, []);
    },

    buildDialogText(warningMessages = [], baseText = "") {
      if (!warningMessages.length) {
        return baseText;
      }
      return `${warningMessages.join("\n")}\n\n${baseText}`;
    },

    cloneRows(rows = []) {
      return JSON.parse(JSON.stringify(rows));
    },

    clearErrors() {
      this.$set(this, "validationErrors", {});
    },
  },
};
</script>

<style lang="scss">
@import "src/assets/scss/aisea/variables";

.crew-assignment-day-off-purchase-carryover {
  .vx-card__body {
    padding: 0 !important;
  }

  .header {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0px 20px 0px 30px;
    border-bottom: 1px solid $aisea-light-white;

    .left-content {
      display: flex;
      align-items: center;
      gap: 30px;

      .title {
        padding: 18px 0px;
        font-size: 18px;
        font-weight: 600;
        font-family: "Hiragino Sans", -apple-system, Roboto, Helvetica,
          sans-serif;
        line-height: 26px;
        color: $aisea-dark-gray;
      }

      .select-year {
        min-width: 240px;

        :deep(.vs__dropdown-toggle) {
          min-height: 38px;
          border-radius: 5px;
          border: 1px solid $aisea-mouse-gray;
        }

        :deep(.vs__selected) {
          display: flex;
          align-items: center;
          position: relative;
          z-index: 1;
        }
      }

      .select-year.vs--disabled .vs__dropdown-toggle,
      .select-year.vs--disabled .vs__search,
      .select-year.vs--disabled .vs__open-indicator {
        background-color: #f5f5f5;
      }

      .select-year.vs--disabled .vs__dropdown-toggle {
        border-color: #dcdcdc;
        color: #bdbdbd;
      }

      .select-year.vs--disabled .vs__selected {
        color: #bdbdbd !important;
      }
    }

    .right-content {
      max-width: 100%;
      overflow-x: hidden;
      display: flex;
      align-items: center;
    }
  }

  .select-option-container {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 16px;
    padding: 8px 20px;
    flex-wrap: wrap;
    border-bottom: 1px solid $aisea-light-white;
  }

  .ag-theme-material {
    .cell-view-only-left {
      display: flex;
      align-items: center;
      justify-content: flex-start !important;
      padding: 8px !important;
    }
    .cell-view-only-right {
      display: flex;
      align-items: center;
      justify-content: flex-end !important;
      padding: 8px !important;
    }
    .cell-view-only-center {
      display: flex;
      align-items: center;
      justify-content: center !important;
      padding: 8px !important;
    }
    .cell-edit-left {
      display: flex;
      align-items: center;
      justify-content: flex-start !important;
      padding: 4px 2px !important;
    }
    .cell-edit-right {
      display: flex;
      align-items: center;
      justify-content: flex-end !important;
      padding: 4px 2px !important;
    }
  }

  .grid-wrapper {
    margin: 20px 20px 16px 20px;
    display: flex;
    border-radius: 6px;
    border: 1px solid $aisea-light-white;
    overflow: hidden;

    .ag-grid-table {
      height: 100%;

      font-size: 14px;
      font-weight: 400;

      .ag-header-cell {
        padding: 0 !important;
      }

      .ag-header-cell-label {
        padding: 8px !important;
        display: flex;
        justify-content: flex-start;
        align-items: center;
      }

      .ag-header-cell-text {
        margin: 0 !important;
        text-align: left;
        white-space: pre-line;
      }

      .ag-cell {
        line-height: 1 !important;
      }

      .ag-cell-value {
        height: 100%;
        width: 100%;
        margin: 0;
        padding: 0;
      }
    }
  }

  .bottom-space {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    padding: 0 24px 20px 24px;
  }
}

.vs-dialog {
  .vs-dialog-accept-button {
    background: #4a90e2 !important;
    border-color: #4a90e2 !important;
  }
}

.vs-dialog-text {
  white-space: pre-line;
  max-height: 60vh;
  overflow-y: auto;
}
</style>
